import React from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  FileText, Video, Youtube, Users, TrendingUp, 
  Upload, Eye, Download, ArrowRight, FileQuestion, Sparkles
} from 'lucide-react';

const Dashboard = () => {
  // Mock statistics
  const stats = [
    { label: 'Course Materials', value: '24', icon: FileText, color: 'bg-red-100 text-red-600' },
    { label: 'Recorded Videos', value: '18', icon: Video, color: 'bg-purple-100 text-purple-600' },
    { label: 'YouTube Videos', value: '32', icon: Youtube, color: 'bg-red-100 text-red-600' },
    { label: 'Total Downloads', value: '1,234', icon: Download, color: 'bg-green-100 text-green-600' }
  ];

  const recentActivity = [
    { action: 'Uploaded', type: 'PDF', name: 'SSC CGL Math Practice Set.pdf', time: '2 hours ago' },
    { action: 'Added', type: 'Video', name: 'Banking Awareness Session 5', time: '5 hours ago' },
    { action: 'Added', type: 'YouTube', name: 'Current Affairs Weekly Update', time: '1 day ago' },
    { action: 'Uploaded', type: 'PDF', name: 'RBI Grade B Economics Notes.pdf', time: '2 days ago' }
  ];

  const quickActions = [
    { 
      title: 'Fix Blog Database', 
      description: '🔧 Fix broken blog posts (if blog not showing)',
      icon: Sparkles,
      link: '/admin/fix-blog',
      color: 'bg-yellow-500 hover:bg-yellow-600'
    },
    { 
      title: 'Generate Current Affairs', 
      description: '✨ AI-powered daily current affairs generation',
      icon: Sparkles,
      link: '/admin/current-affairs',
      color: 'bg-purple-500 hover:bg-purple-600'
    },
    { 
      title: 'Upload Course Material', 
      description: 'Add new study materials and practice papers',
      icon: FileText,
      link: '/admin/materials',
      color: 'bg-red-500 hover:bg-red-600'
    },
    { 
      title: 'Upload Video Lecture', 
      description: 'Add recorded classroom sessions',
      icon: Video,
      link: '/admin/videos',
      color: 'bg-blue-500 hover:bg-blue-600'
    },
    { 
      title: 'Manage Tests', 
      description: 'Create and manage mock tests & test series',
      icon: FileQuestion,
      link: '/admin/tests',
      color: 'bg-green-500 hover:bg-green-600'
    },
    { 
      title: 'Manage PYQ Library', 
      description: '📚 Curate previous year question papers (PDFs)',
      icon: FileText,
      link: '/admin/pyq',
      color: 'bg-orange-500 hover:bg-orange-600'
    },
    {
      title: 'Leads Dashboard',
      description: '📞 View all enquiries & enrollment leads',
      icon: FileText,
      link: '/admin/leads',
      color: 'bg-red-500 hover:bg-red-600'
    }
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600 mt-1">Welcome back! Here's what's happening today.</p>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card key={index}>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">{stat.label}</p>
                      <p className="text-3xl font-bold text-gray-900 mt-1">{stat.value}</p>
                    </div>
                    <div className={`p-3 rounded-full ${stat.color}`}>
                      <Icon size={24} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Quick Actions */}
        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {quickActions.map((action, index) => {
              const Icon = action.icon;
              return (
                <Link key={index} to={action.link}>
                  <Card className="hover:shadow-lg transition-shadow cursor-pointer border-2 hover:border-red-200">
                    <CardContent className="pt-6">
                      <div className={`${action.color} w-12 h-12 rounded-lg flex items-center justify-center mb-4`}>
                        <Icon className="text-white" size={24} />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">{action.title}</h3>
                      <p className="text-gray-600 text-sm mb-4">{action.description}</p>
                      <div className="flex items-center text-red-500 font-medium">
                        <span>Get Started</span>
                        <ArrowRight size={16} className="ml-2" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Recent Activity */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Activity</CardTitle>
            <CardDescription>Latest uploads and updates</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentActivity.map((activity, index) => (
                <div key={index} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
                  <div className="flex items-center space-x-4">
                    <div className="bg-gray-100 p-2 rounded">
                      {activity.type === 'PDF' && <FileText size={20} className="text-red-600" />}
                      {activity.type === 'Video' && <Video size={20} className="text-purple-600" />}
                      {activity.type === 'YouTube' && <Youtube size={20} className="text-red-600" />}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {activity.action} {activity.type}
                      </p>
                      <p className="text-sm text-gray-600">{activity.name}</p>
                    </div>
                  </div>
                  <span className="text-sm text-gray-500">{activity.time}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default Dashboard;
